import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import "./ConfirmationProvider.scss";

type ConfirmationOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
};

type ActiveConfirmation = ConfirmationOptions & {
  resolve: (confirmed: boolean) => void;
};

const ConfirmationContext = createContext<((options: ConfirmationOptions) => Promise<boolean>) | null>(null);

export const useConfirmation = () => {
  const confirm = useContext(ConfirmationContext);
  if (!confirm) throw new Error("useConfirmation must be used within ConfirmationProvider");
  return confirm;
};

export default function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [activeConfirmation, setActiveConfirmation] = useState<ActiveConfirmation | null>(null);
  const activeConfirmationRef = useRef<ActiveConfirmation | null>(null);

  const finishConfirmation = (confirmed: boolean) => {
    const current = activeConfirmationRef.current;
    if (!current) return;
    activeConfirmationRef.current = null;
    setActiveConfirmation(null);
    current.resolve(confirmed);
  };

  const confirm = (options: ConfirmationOptions) => new Promise<boolean>((resolve) => {
    if (activeConfirmationRef.current) {
      activeConfirmationRef.current.resolve(false);
    }
    const nextConfirmation = { ...options, resolve };
    activeConfirmationRef.current = nextConfirmation;
    setActiveConfirmation(nextConfirmation);
  });

  useEffect(() => {
    if (!activeConfirmation) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") finishConfirmation(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeConfirmation]);

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      {activeConfirmation && (
        <div className="confirmation-overlay" onMouseDown={() => finishConfirmation(false)}>
          <section
            className="confirmation-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirmation-title"
            aria-describedby="confirmation-message"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="confirmation-icon" aria-hidden="true">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <h2 id="confirmation-title">{activeConfirmation.title}</h2>
            <p id="confirmation-message">{activeConfirmation.message}</p>
            <div className="confirmation-actions">
              <button type="button" className="confirmation-cancel" autoFocus onClick={() => finishConfirmation(false)}>
                Cancel
              </button>
              <button type="button" className="confirmation-accept" onClick={() => finishConfirmation(true)}>
                {activeConfirmation.confirmLabel ?? "Continue"}
              </button>
            </div>
          </section>
        </div>
      )}
    </ConfirmationContext.Provider>
  );
}
